import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Award, Download, FileText, Medal, Printer, RefreshCw, Trophy } from 'lucide-react';
import StudentCertificate from '../../components/certificates/StudentCertificate';
import { useAuth } from '../../contexts/AuthContext';
import examApi from '../../services/exam.service';
import scholarshipService from '../../services/scholarship.service';
import { ScholarshipRegistration } from '../../types/scholarship';
import {
  StudentCertificateDesignData,
  buildExamCertificateDesignData,
  buildScholarshipCertificateDesignData,
  downloadCertificateHtml,
  openCertificatePrintWindow,
} from '../../utils/studentCertificateDesign';

type CertificateSource = 'exam' | 'scholarship';

interface CertificateRecord {
  id: string;
  source: CertificateSource;
  title: string;
  subtitle: string;
  issuedAt?: string;
  badge: string;
  designData: StudentCertificateDesignData;
}

const CERTIFICATE_READY_STATUSES = new Set([
  'EXAM_SUBMITTED',
  'SHORTLISTED',
  'INTERVIEW_SCHEDULED',
  'INTERVIEW_COMPLETED',
  'SELECTED',
  'NOT_SELECTED',
  'CERTIFICATE_READY',
]);

const getCurrentUser = (user: any) => (user?.user ? user.user : user);

const getStudentDisplayName = (user: any) => {
  const currentUser = getCurrentUser(user);
  return String(
    currentUser?.name ||
      [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ') ||
      currentUser?.fullName ||
      currentUser?.email ||
      'Student',
  ).trim();
};

const formatIssueDate = (value?: string) => {
  if (!value) return 'Recently issued';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Recently issued';
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const isScholarshipCertificateReady = (registration: ScholarshipRegistration) =>
  CERTIFICATE_READY_STATUSES.has(String(registration.status || '').toUpperCase()) ||
  Boolean(registration.certificateId);

const StudentCertificates: React.FC = () => {
  const { user } = useAuth();
  const studentName = useMemo(() => getStudentDisplayName(user), [user]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [certificates, setCertificates] = useState<CertificateRecord[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');

  const loadCertificates = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const [examResult, registrationResult] = await Promise.allSettled([
        examApi.getStudentCertificates(),
        scholarshipService.getMyRegistrations(),
      ]);

      const nextCertificates: CertificateRecord[] = [];
      const loadWarnings: string[] = [];

      if (examResult.status === 'fulfilled' && Array.isArray(examResult.value)) {
        examResult.value.forEach((certificate) => {
          const designData = buildExamCertificateDesignData(certificate, studentName);
          nextCertificates.push({
            id: designData.certificateNumber,
            source: 'exam',
            title: designData.examName || certificate.title || 'Exam Certificate',
            subtitle: [designData.className, designData.subjectName].filter(Boolean).join(' | ') || 'Passed exam',
            issuedAt: certificate.issuedAt,
            badge: designData.achievementBadge || 'Merit',
            designData,
          });
        });
      } else {
        loadWarnings.push('exam certificates');
      }

      if (registrationResult.status === 'fulfilled' && Array.isArray(registrationResult.value)) {
        const eligibleRegistrations = registrationResult.value.filter(isScholarshipCertificateReady);
        const scholarshipResults = await Promise.allSettled(
          eligibleRegistrations.map((registration) =>
            scholarshipService.getCertificate(registration.registrationId),
          ),
        );

        scholarshipResults.forEach((result) => {
          if (result.status !== 'fulfilled') {
            loadWarnings.push('scholarship certificate');
            return;
          }

          const designData = buildScholarshipCertificateDesignData(result.value);
          nextCertificates.push({
            id: designData.certificateNumber,
            source: 'scholarship',
            title: designData.examName || result.value.scholarshipTitle || 'Scholarship Certificate',
            subtitle: designData.subjectName || result.value.classLevel || 'Scholarship',
            issuedAt: result.value.issuedAt,
            badge: designData.achievementBadge || 'Certificate',
            designData,
          });
        });
      } else {
        loadWarnings.push('scholarship registrations');
      }

      nextCertificates.sort((left, right) => {
        const leftTime = left.issuedAt ? new Date(left.issuedAt).getTime() : 0;
        const rightTime = right.issuedAt ? new Date(right.issuedAt).getTime() : 0;
        return rightTime - leftTime;
      });

      setCertificates(nextCertificates);
      setSelectedId((current) => {
        if (current && nextCertificates.some((certificate) => certificate.id === current)) return current;
        return nextCertificates[0]?.id || '';
      });
      setError(
        nextCertificates.length === 0 && loadWarnings.length
          ? 'No generated certificates were found yet.'
          : loadWarnings.length
            ? 'Some certificate sources could not be loaded.'
            : null,
      );
    } catch (err) {
      console.error('Failed to load certificates:', err);
      setCertificates([]);
      setSelectedId('');
      setError('Failed to load certificates.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [studentName]);

  useEffect(() => {
    void loadCertificates(false);
  }, [loadCertificates]);

  const selectedCertificate = useMemo(
    () => certificates.find((certificate) => certificate.id === selectedId) || certificates[0],
    [certificates, selectedId],
  );

  const meritCount = certificates.filter((certificate) =>
    /merit|achievement|winner|rank/i.test(certificate.designData.certificateTitle),
  ).length;
  const participationCount = certificates.length - meritCount;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-8 text-slate-600 sm:px-6 lg:px-8">
        Loading certificates...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,_#f8fafc_0%,_#fff7ed_48%,_#eef2ff_100%)] px-4 py-8 text-slate-950 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <span className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-amber-800">
              <Award className="h-4 w-4" />
              My Certificates
            </span>
            <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
              Achievement certificates
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
              Certificates are generated for completed scholarship participation and passed graded exams.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void loadCertificates(true)}
            disabled={refreshing}
            className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {error && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
            {error}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-lg border border-white/70 bg-white/90 p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-indigo-600" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Total</p>
                <p className="text-2xl font-semibold text-slate-950">{certificates.length}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-white/70 bg-white/90 p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <Trophy className="h-5 w-5 text-amber-600" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Merit</p>
                <p className="text-2xl font-semibold text-slate-950">{meritCount}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-white/70 bg-white/90 p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <Medal className="h-5 w-5 text-emerald-600" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Participation</p>
                <p className="text-2xl font-semibold text-slate-950">{participationCount}</p>
              </div>
            </div>
          </div>
        </div>

        {certificates.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white/90 p-8 text-center text-sm text-slate-600">
            No certificates available yet. Complete scholarship participation or pass an exam to unlock one.
          </div>
        ) : (
          <div className="grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
            <aside className="space-y-3">
              {certificates.map((certificate) => {
                const isSelected = selectedCertificate?.id === certificate.id;
                return (
                  <button
                    key={certificate.id}
                    type="button"
                    onClick={() => setSelectedId(certificate.id)}
                    className={`w-full rounded-lg border bg-white p-4 text-left shadow-sm transition ${
                      isSelected
                        ? 'border-indigo-300 ring-2 ring-indigo-200'
                        : 'border-slate-200 hover:border-indigo-200 hover:bg-indigo-50/30'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-indigo-700">
                          {certificate.source === 'exam' ? 'Exam' : 'Scholarship'}
                        </p>
                        <h2 className="mt-2 text-base font-semibold leading-5 text-slate-950">
                          {certificate.title}
                        </h2>
                        <p className="mt-1 text-sm text-slate-500">{certificate.subtitle}</p>
                      </div>
                      <span className="rounded-lg bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800">
                        {certificate.badge}
                      </span>
                    </div>
                    <div className="mt-4 flex items-center justify-between gap-3 text-xs text-slate-500">
                      <span>{certificate.designData.certificateNumber}</span>
                      <span>{formatIssueDate(certificate.issuedAt)}</span>
                    </div>
                  </button>
                );
              })}
            </aside>

            <section className="min-w-0 rounded-lg border border-white/70 bg-white/90 p-4 shadow-sm">
              {selectedCertificate && (
                <>
                  <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                        Preview
                      </p>
                      <h2 className="mt-1 text-xl font-semibold text-slate-950">
                        {selectedCertificate.designData.certificateTitle}
                      </h2>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => openCertificatePrintWindow(selectedCertificate.designData)}
                        className="inline-flex items-center rounded-lg bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700"
                      >
                        <Printer className="mr-2 h-4 w-4" />
                        Print / Save PDF
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadCertificateHtml(selectedCertificate.designData)}
                        className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Download HTML
                      </button>
                    </div>
                  </div>

                  <div className="overflow-auto rounded-lg border border-slate-200 bg-slate-100 p-4">
                    <StudentCertificate certificate={selectedCertificate.designData} />
                  </div>
                </>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentCertificates;
